import React, { useEffect, useState } from "react";
import { 
    Grid, 
    Paper, 
    Button, 
    Select, 
    MenuItem, 
    InputLabel, 
    FormControl, 
    Typography, 
    TableContainer, 
    Table, 
    TableHead, 
    TableRow, 
    TableCell, 
    TableBody,
    Drawer,
    TextField,
    Box,
    IconButton,
    FormHelperText,
    CircularProgress,
    Alert,
    Collapse
} from "@mui/material";
import { 
    ArrowDropDown, 
    Delete, 
    Edit, 
    PersonAddAlt, 
    Close,
    CheckCircle,
    Error
} from "@mui/icons-material";
import Search from "../../../components/common/search/Search"
import Input from "../../../components/common/input/Input";
import { userApiLoader } from "../../../action/loaderActions";
import axiosInstance from "../../../utils/axios/axiosInstance";
import API from "../../../config/apiconfig";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const StudyGroupTab = () => {
    const dispatch = useDispatch();
    const [data, setData] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState('');
    const [sortedData, setSortedData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [groudId, setGroupId] = useState('');
    const [settings, setSettings] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [courseName, setCoursename] = useState('');
    const [allowedMembers, setIsAllowedMembers] = useState('');
    const [isEditing, setIsEditing] = useState(false); 
    const [submitting, setSubmitting] = useState(false);

    const [courses, setCourses] = useState([]);

    // Pagination state (simplified for 100 items display)
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 100,
        total: 0,
        totalPages: 0
    });

    // Form validation state
    const [validationErrors, setValidationErrors] = useState({});
    const [validationTouched, setValidationTouched] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);

    // Form validation functions
    const validateGroupName = (name) => {
        const trimmedName = name?.trim() || '';
        
        if (!trimmedName) {
            return 'Group name is required';
        }
        if (trimmedName.length < 3) {
            return 'Group name must be at least 3 characters long';
        }
        if (trimmedName.length > 100) {
            return 'Group name must be less than 100 characters';
        }
        return null;
    };

    const validateCourse = (courseId) => {
        if (!courseId) {
            return 'Course selection is required';
        }
        
        const numericId = parseInt(courseId);
        if (isNaN(numericId) || numericId < 1) {
            return 'Please select a valid course';
        }
        
        if (courses.length > 0 && !courses.find(course => course.course_id === numericId)) {
            return 'Selected course does not exist';
        }
        
        return null;
    };

    const validateAllowedMembers = (members) => {
        if (!members) {
            return 'Allowed members is required';
        }
        
        const numericMembers = parseInt(members);
        if (isNaN(numericMembers) || numericMembers < 1) {
            return 'Allowed members must be a positive number';
        }
        
        if (numericMembers > 50) {
            return 'Maximum 50 members allowed per group';
        }
        
        return null;
    };

    // Combined form validation
    const validateForm = () => {
        const errors = {};
        
        const groupNameError = validateGroupName(groupName);
        if (groupNameError) errors.groupName = groupNameError;
        
        const courseError = validateCourse(courseName);
        if (courseError) errors.courseName = courseError;
        
        const membersError = validateAllowedMembers(allowedMembers);
        if (membersError) errors.allowedMembers = membersError;
        
        setValidationErrors(errors);
        const isValid = Object.keys(errors).length === 0;
        setIsFormValid(isValid);
        
        return { isValid, errors };
    };

    // Real-time field validation
    const validateField = (fieldName, value) => {
        let error = null;
        
        switch (fieldName) {
            case 'groupName':
                error = validateGroupName(value);
                break;
            case 'courseName':
                error = validateCourse(value);
                break;
            case 'allowedMembers':
                error = validateAllowedMembers(value);
                break;
            default:
                break;
        }
        
        setValidationErrors(prev => ({
            ...prev,
            [fieldName]: error
        }));
        
        return error;
    };

    // Mark field as touched
    const markFieldAsTouched = (fieldName) => {
        setValidationTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));
    };

    // Handle form input changes with validation
    const handleInputChange = (field, value) => {
        switch (field) {
            case 'groupName':
                setGroupName(value);
                break;
            case 'courseName':
                setCoursename(value);
                break;
            case 'allowedMembers':
                setIsAllowedMembers(value);
                break;
            default:
                break;
        }
        
        // Mark field as touched and validate
        markFieldAsTouched(field);
        validateField(field, value);
    };

    // Helper function to get field error state
    const getFieldError = (fieldName) => {
        return validationTouched[fieldName] && validationErrors[fieldName];
    };

    // Helper function to check if field is valid
    const isFieldValid = (fieldName) => {
        return validationTouched[fieldName] && !validationErrors[fieldName];
    };

    // Validate form whenever form data changes
    useEffect(() => {
        if (Object.keys(validationTouched).length > 0) {
            validateForm();
        }
    }, [groupName, courseName, allowedMembers, validationTouched, courses]);

    const handleSort = (column) => {
        setSelectedFilter(column);
        if (!column) {
            setSortedData(data);
            return;
        }
        const sorted = [...sortedData].sort((a, b) => {
            if (a[column] < b[column]) return -1;
            if (a[column] > b[column]) return 1;
            return 0;
        });
        setSortedData(sorted);
    };

    const handleSearch = (value) => {
        setSearchQuery(value);
        // Reset to first page when searching
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        // Re-fetch data with search query and reset page
        getGroupDetails(false, 1, pagination.perPage || 100, value);
    };

    // Updated getGroupDetails with pagination parameters
    const getGroupDetails = (showToast = false, page = null, limit = null, search = null) => {
        dispatch(userApiLoader({ isApiLoader: true }));
        
        // Use current state values if parameters not provided
        const currentPage = page || pagination.currentPage || 1;
        const currentLimit = limit || pagination.perPage || 100;
        const currentSearch = search !== null ? search : searchQuery;
        
        // Build query parameters
        const params = new URLSearchParams({
            page: currentPage.toString(),
            limit: currentLimit.toString()
        });
        
        if (currentSearch) {
            params.append('search', currentSearch);
        }

        axiosInstance.get(`${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.STUDY_GROUP}?${params}`)
            .then((res) => {
                const responseData = res?.data?.data || [];
                const paginationInfo = res?.data?.pagination || {};
                
                setData(responseData);
                setSortedData(responseData);
                
                // Update pagination state
                setPagination({
                    currentPage: paginationInfo.current_page || currentPage,
                    perPage: paginationInfo.per_page || currentLimit,
                    total: paginationInfo.total || 0,
                    totalPages: Math.ceil((paginationInfo.total || 0) / (paginationInfo.per_page || currentLimit))
                });
                
                dispatch(userApiLoader({ isApiLoader: false }));
                
                // Only show toast if explicitly requested
                if (showToast) {
                    toast.success(res?.data?.message, { autoClose: 6000 });
                }
            })
            .catch((err) => {
                dispatch(userApiLoader({ isApiLoader: false }));
                toast.error(err?.response?.data?.message || "Failed to load study groups", { autoClose: 6000 });
            })
    }

    const fetchCourses = () => {
        axiosInstance.get(`${API.USER_MANAGEMENT_API_BASE_URL}/tools/v1/courses`)
            .then((res) => {
                setCourses(res?.data?.data || []);
            })
            .catch((err) => {
                console.error("Failed to load courses:", err);
            });
    };

    useEffect(() => {
        getGroupDetails();
        fetchCourses();
    }, []);

    // Updated joinGroupHandler: calls v1/studygroup/join with {id: groupId}
    const joinGrouphandler = (row) => {
        dispatch(userApiLoader({ isApiLoader: true }));
        axiosInstance.post(`${API.USER_MANAGEMENT_API_BASE_URL}/tools/v1/studygroup/join`, { id: row.id })
            .then((res) => {
                dispatch(userApiLoader({ isApiLoader: false }));
                toast.success(res?.data?.message || "Successfully joined the study group.", { autoClose: 6000 });
                getGroupDetails(); // Refresh current page data
            })
            .catch((err) => {
                dispatch(userApiLoader({ isApiLoader: false }));
                toast.error(err?.response?.data?.message || "Failed to join the study group.", { autoClose: 6000 });
            })
    }

    const createNewGroupClass = () => {
        // Mark required fields as touched for validation display
        const requiredFields = ['groupName', 'courseName', 'allowedMembers'];
        requiredFields.forEach(field => markFieldAsTouched(field));

        // Validate form before submission
        const { isValid, errors } = validateForm();
        
        if (!isValid) {
            toast.error('Please fix the validation errors before submitting', {
                position: "top-center",
                autoClose: 5000,
            });
            return;
        }

        setSubmitting(true);
        setSettings(false);
        const payload = {
            course_id: courseName,
            group_name: groupName,
            allowed_members: allowedMembers
        }

        dispatch(userApiLoader({ isApiLoader: true }));
        axiosInstance.post(
            `${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.STUDY_GROUP}`,
            payload
        )
            .then((res) => {
                dispatch(userApiLoader({ isApiLoader: false }));
                toast.success(res?.data?.message || "Study group created successfully!", { autoClose: 6000 });
                // Clear form fields
                resetForm();
                // Refresh data without showing additional toast
                getGroupDetails();
            })
            .catch((err) => {
                dispatch(userApiLoader({ isApiLoader: false }));
                toast.error(err?.response?.data?.message || "Failed to create study group.", { autoClose: 6000 });
            })
            .finally(() => {
                setSubmitting(false);
            });
    }

    const handleEdit = (arr) => {
        setIsEditing(true);
        setSettings(true);
        setGroupName(arr.group_name);
        setCoursename(arr.course_id)
        setIsAllowedMembers(arr.allowed_members);
        setGroupId(arr.id);
        
        // Reset validation state for edit mode
        setValidationErrors({});
        setValidationTouched({});
    }

    const updateGroup = () => {
        // Mark required fields as touched for validation display
        const requiredFields = ['groupName', 'courseName', 'allowedMembers'];
        requiredFields.forEach(field => markFieldAsTouched(field));

        // Validate form before submission
        const { isValid, errors } = validateForm();
        
        if (!isValid) {
            toast.error('Please fix the validation errors before submitting', {
                position: "top-center",
                autoClose: 5000,
            });
            return;
        }

        setSubmitting(true);
        setSettings(false);
        setIsEditing(false);
        const payload = {
            id: groudId,
            course_id: courseName,
            group_name: groupName,
            allowed_members: allowedMembers
        };
        dispatch(userApiLoader({ isApiLoader: true }));
        axiosInstance.put(
            `${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.STUDY_GROUP}`,
            payload
        )
            .then((res) => {
                dispatch(userApiLoader({ isApiLoader: false }));
                toast.success(res?.data?.message || "Study group updated successfully!", { autoClose: 6000 });
                // Clear form fields
                resetForm();
                // Refresh data without showing additional toast
                getGroupDetails();
            })
            .catch((err) => {
                dispatch(userApiLoader({ isApiLoader: false }));
                toast.error(err?.response?.data?.message || "Failed to update study group.", { autoClose: 6000 });
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const deleteClass = (arr) => {
        dispatch(userApiLoader({ isApiLoader: true }));
        axiosInstance.delete(
            `${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.STUDY_GROUP}/${arr.id}`
        )
            .then((res) => {
                dispatch(userApiLoader({ isApiLoader: false }));
                toast.success(res?.data?.message || "Study group deleted successfully!", { autoClose: 6000 });
                // Refresh data without showing additional toast
                getGroupDetails();
            })
            .catch((err) => {
                dispatch(userApiLoader({ isApiLoader: false }));
                toast.error(err?.response?.data?.message || "Failed to delete study group.", { autoClose: 6000 });
            })
    }

    // Reset form data and validation
    const resetForm = () => {
        setGroupName('');
        setCoursename('');
        setIsAllowedMembers('');
        setGroupId('');
        setValidationErrors({});
        setValidationTouched({});
        setIsFormValid(false);
        setIsEditing(false);
    };

    // Close modal handler
    const closeModal = () => {
        setSettings(false);
        resetForm();
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (isEditing) {
            updateGroup();
        } else {
            createNewGroupClass();
        }
    };

    return (
        <Grid container spacing={2} style={{ padding: '0px !important' }}>
            <div className="container">
                <div class="css-11ezll3-MuiToolbar-root breadcrumbs">
                    <div className="top-sec">
                        <div className="card custom-card">
                            <div class="card-body ">
                                <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                                    <div class="d-flex align-items-center">
                                        <h3 class="mb-2">Study Group</h3>
                                    </div>
                                    <div class="d-flex align-items-center flex-wrap mb-1">
                                        <Button variant="contained" color="primary" className="!w-fit !px-3 !h-10"
                                            onClick={() => setSettings(true)}>
                                            + Add New Group
                                        </Button>
                                    </div>
                                </div>

                                <div className="row filter-panel mt-4">
                                    <div className="col-md-4">
                                        <div className="serarch-bar">
                                            <Search value={searchQuery} handleInputChange={(e) => handleSearch(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="col-md-6"></div>
                                    <div className="col-md-2">
                                        <div className="w-full flex items-center justify-end gap-2">
                                            <FormControl fullWidth className="filter">
                                                <InputLabel>Filter</InputLabel>
                                                <Select
                                                    value={selectedFilter}
                                                    onChange={(e) => handleSort(e.target.value)}
                                                    label="Filter"
                                                    IconComponent={(props) => (
                                                        <ArrowDropDown {...props} style={{ color: '#c942b1' }} />
                                                    )}
                                                    style={{ height: 40 }}
                                                >
                                                    <MenuItem value="">None</MenuItem>
                                                    <MenuItem value="groupName">Group Name</MenuItem>
                                                    <MenuItem value="members">No. of Members</MenuItem>
                                                    <MenuItem value="courseName">Course Name</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-12">
                                        <div class="table-responsive mb-0">
                                            <TableContainer className="table table-hover table-bordered mb-0 text-md-nowrap text-lg-nowrap text-xl-nowrap table-striped">
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Group Name</TableCell>
                                                            <TableCell>Allowed Members</TableCell>
                                                            <TableCell>Enrolled Members</TableCell>
                                                            <TableCell>Course Name</TableCell>
                                                            <TableCell align="center">Action</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {sortedData.map((row, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell>{row.group_name}</TableCell>
                                                                <TableCell>{row.allowed_members}</TableCell>
                                                                <TableCell>{row.enrolled_members}</TableCell>
                                                                <TableCell>{row.course_name}</TableCell>
                                                                <TableCell align="center">
                                                                    {row.is_owned === 1 ? (
                                                                        <div className="flex items-center gap-1 active-btn">
                                                                            <div
                                                                                className="btn btn-indigo btn-icon"
                                                                                onClick={() => handleEdit(row)}
                                                                            >
                                                                                <Edit color="primary" fontSize="small" />
                                                                            </div>
                                                                            <div
                                                                                className="btn btn-denger btn-icon"
                                                                                onClick={() => deleteClass(row)}
                                                                            >
                                                                                <Delete color="primary" fontSize="small" />
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        row.is_joined === 1 ? (
                                                                            <Button variant="outlined" disabled>
                                                                                Joined
                                                                            </Button>
                                                                        ) : (
                                                                            <Button
                                                                                variant="contained"
                                                                                color="primary"
                                                                                startIcon={<PersonAddAlt />}
                                                                                onClick={() => joinGrouphandler(row)}
                                                                            >
                                                                                Join
                                                                            </Button>
                                                                        )
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </div>

                                        {/* Data Info */}
                                        {data.length > 0 && (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                alignItems: 'center', 
                                                mt: 3,
                                                p: 2
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                    Showing {data.length} of {pagination.total} total entries
                                                </Typography>
                                            </Box>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Material-UI Drawer Form - Using TodoList style */}
            <Drawer
                anchor="right"
                open={settings}
                onClose={closeModal}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 400, md: 450 },
                        maxWidth: '90vw',
                        fontFamily: '"Saira", sans-serif'
                    }
                }}
            >
                <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{
                        background: '#ce55b9',
                        color: 'white',
                        p: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: '"Saira", sans-serif' }}>
                                {isEditing ? 'Update' : 'Add'} Group
                            </Typography>
                            {/* Form validation indicator */}
                            {Object.keys(validationTouched).length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isFormValid ? (
                                        <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                                    ) : (
                                        <Error sx={{ color: '#ffeb3b', fontSize: 20 }} />
                                    )}
                                </Box>
                            )}
                        </Box>
                        <IconButton
                            onClick={closeModal}
                            disabled={submitting}
                            sx={{
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>

                    {/* Form validation summary */}
                

                    {/* Content */}
                    <Box sx={{
                        flex: 1,
                        p: 3,
                        overflow: 'auto',
                        background: '#ffffff'
                    }}>
                        <TextField
                            fullWidth
                            label="Group Name"
                            value={groupName}
                            onChange={(e) => handleInputChange('groupName', e.target.value)}
                            onBlur={() => markFieldAsTouched('groupName')}
                            required
                            error={!!getFieldError('groupName')}
                            helperText={getFieldError('groupName') || 'Enter a descriptive name for your study group (3-100 characters)'}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontFamily: '"Saira", sans-serif',
                                    '&.Mui-error': {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#f44336',
                                        }
                                    },
                                    '&.Mui-focused': {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isFieldValid('groupName') ? '#4caf50' : '#ce55b9',
                                        }
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: '"Saira", sans-serif',
                                    '&.Mui-focused': {
                                        color: isFieldValid('groupName') ? '#4caf50' : '#ce55b9'
                                    }
                                },
                                '& .MuiFormHelperText-root': {
                                    fontFamily: '"Saira", sans-serif',
                                    '&.Mui-error': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }
                                }
                            }}
                            disabled={submitting}
                            placeholder="Enter study group name"
                            InputProps={{
                                endAdornment: isFieldValid('groupName') ? (
                                    <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                                ) : getFieldError('groupName') ? (
                                    <Error sx={{ color: '#f44336', fontSize: 20 }} />
                                ) : null
                            }}
                        />

                        <FormControl 
                            fullWidth 
                            sx={{ mb: 3 }}
                            error={!!getFieldError('courseName')}
                        >
                            <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>Select Course</InputLabel>
                            <Select
                                value={courseName}
                                label="Select Course"
                                onChange={(e) => handleInputChange('courseName', e.target.value)}
                                onBlur={() => markFieldAsTouched('courseName')}
                                disabled={submitting}
                                IconComponent={(props) => (
                                    <ArrowDropDown {...props} style={{ color: "#c942b1" }} />
                                )}
                                sx={{
                                    borderRadius: 2,
                                    fontFamily: '"Saira", sans-serif',
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isFieldValid('courseName') ? '#4caf50' : '#ce55b9',
                                    }
                                }}
                                endAdornment={
                                    isFieldValid('courseName') ? (
                                        <CheckCircle sx={{ color: '#4caf50', fontSize: 20, mr: 2 }} />
                                    ) : getFieldError('courseName') ? (
                                        <Error sx={{ color: '#f44336', fontSize: 20, mr: 2 }} />
                                    ) : null
                                }
                            >
                                <MenuItem value="" sx={{ fontFamily: '"Saira", sans-serif' }}>Select</MenuItem>
                                {courses.map((course) => (
                                    <MenuItem key={course.course_id} value={course.course_id} sx={{ fontFamily: '"Saira", sans-serif' }}>
                                        {course.course_name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {getFieldError('courseName') && (
                                <FormHelperText sx={{ fontFamily: '"Saira", sans-serif' }}>
                                    {getFieldError('courseName')}
                                </FormHelperText>
                            )}
                        </FormControl>

                        <TextField
                            fullWidth
                            type="number"
                            label="Allowed Members"
                            value={allowedMembers}
                            onChange={(e) => handleInputChange('allowedMembers', e.target.value)}
                            onBlur={() => markFieldAsTouched('allowedMembers')}
                            required
                            error={!!getFieldError('allowedMembers')}
                            helperText={getFieldError('allowedMembers') || 'Enter the maximum number of members (1-50)'}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontFamily: '"Saira", sans-serif',
                                    '&.Mui-error': {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#f44336',
                                        }
                                    },
                                    '&.Mui-focused': {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isFieldValid('allowedMembers') ? '#4caf50' : '#ce55b9',
                                        }
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: '"Saira", sans-serif',
                                    '&.Mui-focused': {
                                        color: isFieldValid('allowedMembers') ? '#4caf50' : '#ce55b9'
                                    }
                                },
                                '& .MuiFormHelperText-root': {
                                    fontFamily: '"Saira", sans-serif'
                                }
                            }}
                            disabled={submitting}
                            placeholder="Enter allowed members"
                            inputProps={{
                                min: 1,
                                max: 50
                            }}
                            InputProps={{
                                endAdornment: isFieldValid('allowedMembers') ? (
                                    <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                                ) : getFieldError('allowedMembers') ? (
                                    <Error sx={{ color: '#f44336', fontSize: 20 }} />
                                ) : null
                            }}
                        />
                    </Box>

                    {/* Footer Actions */}
                    <Box sx={{
                        p: 3,
                        borderTop: '1px solid rgba(0,0,0,0.1)',
                        background: 'white',
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'flex-end'
                    }}>
                        <Button
                            onClick={closeModal}
                            disabled={submitting}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: '"Saira", sans-serif',
                                px: 3,
                                py: 1.5,
                                minWidth: 100
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting || (!isFormValid && Object.keys(validationTouched).length > 0)}
                            sx={{
                                background: isFormValid ? '#4caf50' : '#ce55b9',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: '"Saira", sans-serif',
                                px: 3,
                                py: 1.5,
                                minWidth: 140,
                                '&:hover': {
                                    background: isFormValid ? '#b8479a' : '#b8479a',
                                },
                                '&:disabled': {
                                    background: '#b8479a',
                                    color: 'white'
                                }
                            }}
                        >
                            {submitting ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} sx={{ color: 'white' }} />
                                    <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                                        {isEditing ? 'Updating...' : 'Creating...'}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isFormValid && <CheckCircle sx={{ fontSize: 18 }} />}
                                    <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                                        {isEditing ? 'Update' : 'Add Group'}
                                    </Typography>
                                </Box>
                            )}
                        </Button>
                    </Box>
                </form>
            </Drawer>
        </Grid>
    )
}

export default StudyGroupTab;